// ============================================================================
// Favorites Context - Centralized favorites management
// ============================================================================
// This context provides a single source of truth for favorites,
// syncing once with the database when the user is authenticated.
// Components consume this context instead of running their own sync hooks.

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useState,
  ReactNode,
} from "react";
import { useCollegeStore } from "../store/useCollegeStore";
import { UserAuth } from "./AuthContext";
import {
  getFavorites,
  addToFavorites,
  removeFromFavorites,
} from "../services/college";

// ============================================================================
// Types
// ============================================================================

interface FavoritesContextType {
  favorites: string[];
  toggleFavorite: (collegeId: string, collegeName?: string) => void;
  isFavorite: (collegeId: string) => boolean;
  isInitialized: boolean;
}

// ============================================================================
// Context
// ============================================================================

const FavoritesContext = createContext<FavoritesContextType | undefined>(
  undefined,
);

// ============================================================================
// Provider
// ============================================================================

interface FavoritesProviderProps {
  children: ReactNode;
}

export function FavoritesProvider({ children }: FavoritesProviderProps) {
  const { session } = UserAuth();
  const {
    favorites,
    setFavorites,
    toggleFavorite: zustandToggle,
  } = useCollegeStore();
  const [isInitialized, setIsInitialized] = useState(false);
  const isSyncing = useRef(false);

  // Initialize: Fetch from DB ONCE when user is authenticated
  // Skip for counselors since favorites are a student-only feature
  useEffect(() => {
    async function initializeFavorites() {
      if (!session?.user?.id || isInitialized || isSyncing.current) {
        return;
      }

      // Skip favorites for counselors - they don't have student profiles
      const userRole = session.user.user_metadata?.role;
      if (userRole === "counselor") {
        setIsInitialized(true);
        return;
      }

      isSyncing.current = true;

      try {
        // Get favorites from Supabase (single source of truth)
        const dbFavorites = await getFavorites(session.user.id);

        // Replace local favorites with DB favorites
        setFavorites(dbFavorites);
        setIsInitialized(true);
      } catch (error) {
        console.error("❌ [FavoritesContext] Initialization error:", error);
      } finally {
        isSyncing.current = false;
      }
    }

    initializeFavorites();
  }, [session?.user?.id, setFavorites, isInitialized]);

  // Sync add to DB (background, fire-and-forget)
  const syncAddToDb = useCallback(
    async (collegeId: string, collegeName: string) => {
      if (!session?.user?.id) return;

      try {
        await addToFavorites(session.user.id, collegeId, collegeName);
      } catch (error: any) {
        // Handle FK constraint error gracefully
        if (
          error?.code !== "23503" &&
          !error?.message?.includes("foreign key constraint")
        ) {
          console.error(
            "❌ [FavoritesContext] Failed to add:",
            collegeId,
            error,
          );
        }
      }
    },
    [session?.user?.id],
  );

  // Sync remove from DB (background, fire-and-forget)
  const syncRemoveFromDb = useCallback(
    async (collegeId: string) => {
      if (!session?.user?.id) return;

      try {
        await removeFromFavorites(session.user.id, collegeId);
      } catch (error) {
        console.error(
          "❌ [FavoritesContext] Failed to remove:",
          collegeId,
          error,
        );
      }
    },
    [session?.user?.id],
  );

  // Toggle favorite with optimistic update and background sync
  const toggleFavorite = useCallback(
    (collegeId: string, collegeName?: string) => {
      const normalizedId = String(collegeId);
      const currentlyFavorite = favorites.includes(normalizedId);

      // Optimistic update (immediate UI change)
      zustandToggle(normalizedId);

      // Background sync to DB
      if (currentlyFavorite) {
        syncRemoveFromDb(normalizedId);
      } else {
        syncAddToDb(normalizedId, collegeName || `College ${collegeId}`);
      }
    },
    [favorites, zustandToggle, syncAddToDb, syncRemoveFromDb],
  );

  // Check if a college is favorited
  const isFavorite = useCallback(
    (collegeId: string) => favorites.includes(String(collegeId)),
    [favorites],
  );

  const value: FavoritesContextType = {
    favorites,
    toggleFavorite,
    isFavorite,
    isInitialized,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access favorites from context.
 * Must be used within FavoritesProvider.
 */
export function useFavorites(): FavoritesContextType {
  const context = useContext(FavoritesContext);
  if (context === undefined) {
    throw new Error("useFavorites must be used within a FavoritesProvider");
  }
  return context;
}
